import { useEffect, useState, type ReactElement } from 'react';
import { Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { RedactedText } from '@/components/redacted-email';
import { useFetch } from '@/hooks/use-fetch';
import { parseApiError } from '@/lib/api-error';
import { emit } from '@/lib/analytics';
import { getOsCopyShortcut } from '@/lib/platform';

const PRE_MINT_PLACEHOLDER =
  "This action copies a one-time login to your account. Don't share it with anyone.";

interface MintResponse {
  code: string;
  expiresAt: number;
  instructionBlock: string;
  warningCopy: string;
}

interface BootstrapCodeDialogProps {
  trigger: ReactElement;
  sandboxSessionPublicId?: string;
  workspaceId?: string;
  /**
   * AIT-393: explicit mint target, sent as `X-Workspace-Id`. `useFetch` only
   * injects the cached `activeWorkspace`, which can disagree with the URL
   * (AIT-207) — callers that know the workspace the user is looking at pass
   * it here so the credentials are scoped to that one.
   */
  targetWorkspaceId?: string;
  /** AIT-213: 'dashboard' selects the dashboard-card instruction block server-side. */
  variant?: 'dashboard';
}

// Server-backed bootstrap-code reveal-panel modal.
//
// Behavior contract — locked by the 2026-04-30 copy-hardening spec:
//   1. Opening the dialog mints a fresh code via POST /auth/bootstrap/mint
//      with an AbortController that cancels on dismiss. Every open mints; no
//      cache, no supersession (spec D2).
//   2. The "Copy to clipboard" action button does only navigator.clipboard
//      .writeText + analytics + close. No fetch in the user-gesture window
//      (spec D1).
//   3. If writeText rejects, the dialog body swaps to a fallback textarea
//      auto-selected on render (spec D5). sandbox_instructions_copy_failed
//      fires; sandbox_instructions_copied does NOT.
export function BootstrapCodeDialog({
  trigger,
  sandboxSessionPublicId,
  workspaceId,
  targetWorkspaceId,
  variant,
}: BootstrapCodeDialogProps) {
  const fetchWithAuth = useFetch();
  const [open, setOpen] = useState(false);
  const [minting, setMinting] = useState(false);
  const [mint, setMint] = useState<MintResponse | null>(null);
  const [mintError, setMintError] = useState<string | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);

  function resetDialogState() {
    setMint(null);
    setMintError(null);
    setCopyFailed(false);
  }

  useEffect(() => {
    if (!open) {
      resetDialogState();
      return;
    }
    const controller = new AbortController();
    setMinting(true);
    setMintError(null);

    void (async () => {
      try {
        const res = await fetchWithAuth('/auth/bootstrap/mint', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // use-fetch treats its injection as a default, so an explicit
            // target wins (AIT-207).
            ...(targetWorkspaceId ? { 'X-Workspace-Id': targetWorkspaceId } : {}),
          },
          body: JSON.stringify({
            ...(sandboxSessionPublicId ? { sandboxSessionPublicId } : {}),
            ...(variant ? { variant } : {}),
          }),
          signal: controller.signal,
        });

        if (!res.ok) {
          const body = await res.json().catch(() => null);
          const apiError = parseApiError(
            res.status,
            body,
            'Failed to generate instructions. Please try again.',
          );
          setMintError(apiError.userMessage);
          return;
        }

        const data = (await res.json()) as MintResponse;
        setMint(data);
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return;
        setMintError('Failed to generate instructions. Please try again.');
      } finally {
        setMinting(false);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [open, fetchWithAuth, sandboxSessionPublicId, targetWorkspaceId, variant]);

  // Auto-select is handled by the textarea's autoFocus + onFocus={select}
  // (see render below) — deterministic, no race with the dialog focus trap.

  function handleOpenChange(nextOpen: boolean) {
    if (minting) return;
    setOpen(nextOpen);
  }

  async function handleCopy() {
    if (!mint) return;
    try {
      await navigator.clipboard.writeText(mint.instructionBlock);
      toast.success('Instructions copied. Paste into your AI.');
      if (workspaceId && sandboxSessionPublicId) {
        emit('sandbox_instructions_copied', {
          workspace_id: workspaceId,
          session_public_id: sandboxSessionPublicId,
        });
      }
      setOpen(false);
    } catch (err) {
      const name = (err as { name?: string })?.name ?? 'Error';
      const message = (err as { message?: string })?.message;
      setCopyFailed(true);
      if (workspaceId) {
        emit('sandbox_instructions_copy_failed', {
          workspace_id: workspaceId,
          session_public_id: sandboxSessionPublicId,
          reason: name,
          error_message: message ? message.slice(0, 200) : undefined,
        });
      }
    }
  }

  const shortcut = getOsCopyShortcut();
  const description = mintError ?? mint?.warningCopy ?? PRE_MINT_PLACEHOLDER;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger render={trigger} />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {copyFailed ? `Copy failed. Press ${shortcut} to copy manually.` : 'Heads Up'}
          </AlertDialogTitle>
          {!copyFailed && (
            <AlertDialogDescription className="whitespace-pre-line">
              <RedactedText text={description} />
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>

        {copyFailed && mint && (
          <textarea
            readOnly
            autoFocus
            onFocus={(e) => e.currentTarget.select()}
            value={mint.instructionBlock}
            aria-label="AI instructions"
            className="w-full h-48 rounded-md border bg-muted px-3 py-2 font-mono text-sm"
          />
        )}

        <AlertDialogFooter>
          {copyFailed ? (
            <AlertDialogAction onClick={() => setOpen(false)}>
              Done
            </AlertDialogAction>
          ) : (
            <>
              <AlertDialogCancel disabled={minting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCopy}
                disabled={minting || mint === null || mintError !== null}
              >
                {minting ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Copy className="size-3.5" />
                )}
                Copy to clipboard
              </AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
